import { CookieService, LocalstorageService } from 'ngx-unificator/services';
import { delay, distinctUntilChanged, filter, switchMap, takeWhile, tap } from 'rxjs/operators';
import { PlatformService } from '../../core/services/platform.service';
import { UserService } from '../../core/services/user/user.service';
import { UserGroupsService } from '../../core/services/user/user-groups.service';
import { ActivatedRoute, Router } from '@angular/router';
import { inject } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { UserBonusesService } from '../../core/services/user/user-bonuses.service';
import { PaymentMode, UserPaymentModeService } from '../../core/services/user/user-payment-mode.service';
import { StaticPageService } from '../static-page/static-page.service';
import { LootboxService } from '../../core/services/lootbox/lootbox.service';
import { TimeService } from '../../core/services/time.service';
import { TournamentsService } from '../tournaments/tournaments.service';
import { GlobalEventsService } from '../../core/services/global-events.service';
import { LocalHistoryService } from '../../core/services/local-history.service';
import { TranslationService } from '../../core/services/translation/translation.service';
import { WindowService } from '../../core/services/window.service';
import { BonusStatus, BonusType } from '../../core/services/user/data/user-bonuses.data';
import { UserStorageRedirectService } from '../../core/services/user/user-storage-redirect.service';
import { ModalService } from '../../core/modal/modal.service';
import { ALL_WELCOME_CLAIMED } from '../../core/services/user/data/user-group.data';

type ActionConfigMap<T extends Record<string, any>> = {
  [K in keyof T]: {
    url: string;
    isNeedRedirect?: boolean;
    isNativeUrl?: boolean;
    isAnchorUrl?: boolean;
  };
};

export enum EventType {
  First = 'first',
  Second = 'second',
  Third = 'third'
}

type BasePromoType = {
  HuntModal: {
    BonusGroupClaimed: string;
    BonusGroupCatch: string;
    Title: string;
    Desc: string;
    Rule: string;
    AboutPromo: string;
    CongratsTitle: string;
    Prize: string;
    FinalDesc: string;
    Code: string;
    DepositRule: string;
    Terms: string;
  };
  WelcomePromoModal: {
    Title: string;
    Desc: string;
    Prize: string;
    Rule: string;
  };
}

export const ALREADY_SPINED_PROMO = 'already_spined_promo';

export abstract class BasePromo<TPageType = unknown> {

  /**
   * Abstract event name for hunt(halloween, easter-eggs ...)
   */
  public abstract eventName: string;

  /**
   * Event type elements
   */
  public eventType = EventType;

  /**
   * Event collected list
   */
  public eventList: unknown[] = null;

  /**
   * Event Loaded
   */
  private _eventLoaded = null;

  public huntModalData: any = {};

  private _platform: PlatformService = inject(PlatformService);
  private _events: GlobalEventsService = inject(GlobalEventsService);
  public cookie: CookieService = inject(CookieService);
  public storage: LocalstorageService = inject(LocalstorageService);
  public group: UserGroupsService = inject(UserGroupsService);
  public bonuses: UserBonusesService = inject(UserBonusesService);
  public user: UserService = inject(UserService);
  public paymentMode: UserPaymentModeService = inject(UserPaymentModeService);
  public page: StaticPageService = inject(StaticPageService);
  public lootboxService: LootboxService = inject(LootboxService);
  public time: TimeService = inject(TimeService);
  public tournaments: TournamentsService = inject(TournamentsService);
  public local: LocalHistoryService = inject(LocalHistoryService);
  public router: Router = inject(Router);
  public route: ActivatedRoute = inject(ActivatedRoute);
  public translate: TranslationService = inject(TranslationService);
  public window: WindowService = inject(WindowService);
  public platform: PlatformService = inject(PlatformService);
  public redirect: UserStorageRedirectService = inject(UserStorageRedirectService);
  public modals: ModalService = inject(ModalService);

  constructor() {
  }

  public get eventEnable(): boolean {
    return this._eventLoaded;
  }

  public get isFirstExist(): boolean {
    return this._eventLoaded && !this.eventList.includes(this.eventType.First) && this.user.auth;
  }

  public get isSecondExist(): boolean {
    return this._eventLoaded && !this.eventList.includes(this.eventType.Second) && this.user.auth;
  }

  public get isThirdExist(): boolean {
    return this._eventLoaded && !this.eventList.includes(this.eventType.Third) && this.user.auth;
  }

  /**
   * Fetch necessary data for promo
   * @private
   */
  public fetchPromoData$(): Observable<[any, any[], unknown]> {
    return combineLatest([
      this.page.item({
        slug: this.eventName,
      }),
      ...this.loadNecessaryBonuses$(),
    ]);
  }

  public loadNecessaryBonuses$(): [Observable<any[]>, Observable<unknown>] {
    return [
      this.lootboxService.loadOriginalLootboxList$(),
      this.bonuses.loadOriginalBonusList$(),
    ];
  }

  /**
   * Get filtered last promo lootbox
   * @param arrayToCheck
   * @param ids
   * @param status
   * @private
   */
  public getFilteredPromoLootbox(arrayToCheck: any[], ids: string[], status: BonusStatus): any {
    return arrayToCheck?.filter(lootbox => ids.includes(lootbox?.title) && status === lootbox?.status)
      .sort((a, b) => b.issuedAt - a.issuedAt)[0];
  }

  public getFilteredCashWelcomeBonuses(arrayToCheck: any[]): any {
    return arrayToCheck?.filter(bonus =>
      bonus?.active && bonus?.type === BonusType.CASH && bonus?.isWelcome &&
      !Boolean(this.storage.get(ALREADY_SPINED_PROMO)))
      .sort((a, b) => b.createdAt - a.createdAt)[0];
  }

  /**
   * Get filtered welcome freespins
   * @param arrayToCheck
   * @param isAlreadySpinCookie
   * @private
   */
  public getFilteredFreespinsWelcomeBonuses(arrayToCheck: any[]): any {
    return arrayToCheck?.filter(bonus =>
      bonus?.active && bonus?.type === BonusType.FREESPIN && bonus?.isWelcome &&
      !Boolean(this.storage.get(ALREADY_SPINED_PROMO)))
      .sort((a, b) => b.createdAt - a.createdAt)[0];
  }

  /**
   * Check if user already activated promo lootbox
   * @param promoActivatedLootbox
   * @param ids
   * @private
   */
  public checkActivatedTodayLootbox(promoActivatedLootbox: any, ids: string[]): boolean {
    return ids.includes(promoActivatedLootbox?.title) &&
      this.time.isTodayDate(promoActivatedLootbox?.issuedAt);
  }

  public addEventToCollection(type: EventType) {
    if (this._platform.isBrowser) {
      this.eventList.push(type);
      this.cookie.set(this.eventName, JSON.stringify(this.eventList), 999, '/');
      if (this.eventList.length === 3) {
        this.group.addToGroup(this.huntModalData.BonusGroupCatch)?.subscribe();
      }
      this.openHuntModal(type);
    }
  }

  public initHuntEvent(HuntModal: any) {
    this.huntModalData = HuntModal;
    if (!this.group.isExistGroup(this.huntModalData?.BonusGroupClaimed)) {
      if (this.group.isExistGroup(this.huntModalData?.BonusGroupCatch)) {
        this.eventList = [this.eventType.First, this.eventType.Second, this.eventType.Third];
      } else {
        this.eventList = this.cookie.check(this.eventName) ? JSON.parse(this.cookie.get(this.eventName)) : [];
      }
      this._eventLoaded = true;
    } else {
      this._eventLoaded = false;
    }
  }

  public initWelcomePromoModal() {
    if (this._platform.isBrowser) {
      this._checkShouldOpenWelcomeModal$().subscribe();
    }
  }

  public async onAction<
    TAction extends string,
    TConfig extends ActionConfigMap<Record<TAction, any>>
  >(
    config: TConfig[TAction],
  ): Promise<void> {
    const { url, isNeedRedirect, isAnchorUrl, isNativeUrl } = config;

    if (isAnchorUrl) {
      await this.router.navigate([url], { fragment: 'special' });
      return;
    }

    if (isNativeUrl) {
      this.window.nativeWindow.open(url, '_blank');
      return;
    }

    if (isNeedRedirect) {
      this.redirect.addRedirectRoute();
    }

    await this.router.navigateByUrl(url);
  }


  private _checkShouldOpenWelcomeModal$(): Observable<boolean> {
    const routesIgnore = ['403', '404', 'register', 'login', 'deposit', 'cashout', 'games', 'promo', 'play'];
    return this._events.routerNavigationEnd$
      .pipe(
        takeWhile(() => !this.cookie.check('welcome-modal-promo'), true),
        distinctUntilChanged(),
        delay(120000),
        filter(() => {
          const hasWelcomeModalCookie = this.cookie.check('welcome-modal-promo');
          const shouldOpenModal = this.user.auth && !hasWelcomeModalCookie;
          return shouldOpenModal && !routesIgnore.some(route => this.router.url.includes(route));
        }),
        switchMap(() => this.checkActivatedBonuses()),
      );
  }

  public abstract openHuntModal?(type: EventType): void;

  public abstract checkActivatedBonuses?(): Observable<any> | Promise<any>;

}
